import { Suspense } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { CreditsProvider } from "@/hooks/useCredits";
import CookieConsentBanner from "./components/legal/CookieConsentBanner";
import { EducationalDisclaimer } from "./components/legal/EducationalDisclaimer";
import PaywallGate from "./components/PaywallGate";
import { PaymentTestModeBanner } from "./components/payments/PaymentTestModeBanner";
import { CreditEmptyBanner } from "./components/credits/CreditEmptyBanner";
import { AdminGrantModal } from "./components/credits/AdminGrantModal";
import { InstallAppBanner } from "./components/InstallAppBanner";
import { PwaUpdatePrompt } from "./components/PwaUpdatePrompt";
import { SiteFooter } from "./components/SiteFooter";

// Route-level code splitting: each page becomes its own chunk so the initial
// bundle (e.g. on /auth) no longer ships every route's code + dependencies
// (Stripe, recharts, etc.). This dramatically reduces unused JS on first load.
const Index = lazy(() => import("./pages/Index.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const AuthPage = lazy(() => import("./pages/Auth.tsx"));
const Performance = lazy(() => import("./pages/Performance.tsx"));
const SettingsPage = lazy(() => import("./pages/Settings.tsx"));
const Journal = lazy(() => import("./pages/Journal.tsx"));
const Me = lazy(() => import("./pages/Me.tsx"));
const HowItWorks = lazy(() => import("./pages/HowItWorks.tsx"));
const News = lazy(() => import("./pages/News.tsx"));
const CotBacktest = lazy(() => import("./pages/CotBacktest.tsx"));
const AnalyzeIndex = lazy(() => import("./pages/AnalyzeIndex.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy.tsx"));
const CookiePolicy = lazy(() => import("./pages/legal/CookiePolicy.tsx"));
const Terms = lazy(() => import("./pages/legal/Terms.tsx"));
const Pricing = lazy(() => import("./pages/Pricing.tsx"));
const Credits = lazy(() => import("./pages/Credits.tsx"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn.tsx"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout.tsx"));
const AdminOverview = lazy(() => import("./pages/admin/AdminOverview.tsx"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers.tsx"));
const AdminSubscriptions = lazy(() => import("./pages/admin/AdminSubscriptions.tsx"));
const AdminContent = lazy(() => import("./pages/admin/AdminContent.tsx"));
const AdminDebug = lazy(() => import("./pages/admin/AdminDebug.tsx"));
const AiDebug = lazy(() => import("./pages/AiDebug.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const MarketAI = lazy(() => import("./pages/MarketAI.tsx"));
const AiMemory = lazy(() => import("./pages/AiMemory.tsx"));
const Maintenance = lazy(() => import("./pages/Maintenance.tsx"));
const Alerts = lazy(() => import("./pages/Alerts.tsx"));
const Install = lazy(() => import("./pages/Install.tsx"));
const SystemMonitor = lazy(() => import('./pages/SystemMonitor.tsx'));
const Arhitectura = lazy(() => import('./pages/Arhitectura.tsx'));
const AnomalyHunter = lazy(() => import('./pages/AnomalyHunter.tsx'));

// Toggle this to false when maintenance is over.
const MAINTENANCE_MODE = false;
// Paths still reachable during maintenance (admin can log in & manage).
const MAINTENANCE_ALLOWLIST = ["/auth", "/admin"];

const queryClient = new QueryClient();

// Minimal fallback that blends with the app background â€” avoids any visible
// flash while a route chunk is being fetched.
const RouteFallback = () => <div className="min-h-screen bg-background" />;

const App = () => {
  if (MAINTENANCE_MODE) {
    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    const allowed = MAINTENANCE_ALLOWLIST.some((p) => path.startsWith(p));
    if (!allowed) {
      return (
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <Maintenance />
        </Suspense>
      );
    }
  }
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <CreditsProvider>
          <PaymentTestModeBanner />
          <CreditEmptyBanner />
          <AdminGrantModal />
          <InstallAppBanner />
          <PwaUpdatePrompt />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            <Route path="/analyze" element={<Navigate to="/" replace />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/credits" element={<PaywallGate requireAuthOnly><Credits /></PaywallGate>} />
            <Route path="/checkout/return" element={<PaywallGate requireAuthOnly><CheckoutReturn /></PaywallGate>} />
            <Route path="/performance" element={<PaywallGate><Performance /></PaywallGate>} />
            <Route path="/journal" element={<PaywallGate><Journal /></PaywallGate>} />
            <Route path="/settings" element={<PaywallGate><SettingsPage /></PaywallGate>} />
            <Route path="/news" element={<PaywallGate><News /></PaywallGate>} />
            <Route path="/market-ai" element={<PaywallGate><MarketAI /></PaywallGate>} />
            <Route path="/market-ai/memory" element={<PaywallGate><AiMemory /></PaywallGate>} />
            <Route path="/cot-backtest" element={<PaywallGate><CotBacktest /></PaywallGate>} />
            <Route path="/indices/analyze" element={<PaywallGate><AnalyzeIndex /></PaywallGate>} />
            <Route path="/alerts" element={<PaywallGate><Alerts /></PaywallGate>} />
            <Route path='/system-monitor' element={<PaywallGate requireAuthOnly><SystemMonitor /></PaywallGate>} />
              <Route path='/arhitectura' element={<PaywallGate requireAuthOnly><Arhitectura /></PaywallGate>} />
            <Route path='/anomaly-hunter' element={<PaywallGate requireAuthOnly><AnomalyHunter /></PaywallGate>} />
            <Route path="/me" element={<PaywallGate><Me /></PaywallGate>} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/ai-debug" element={<PaywallGate requireAuthOnly><AiDebug /></PaywallGate>} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminOverview />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="debug" element={<AdminDebug />} />
            </Route>
            <Route path="/about" element={<About />} />
            <Route path="/install" element={<Install />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/terms" element={<Terms />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          <SiteFooter />
          <EducationalDisclaimer />
          <CookieConsentBanner />
          </CreditsProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;

